// Authentication and User Management
// Cognito is the sole authentication provider.
// This module provides session bridging for the app's role-based UI.

import { agentSessionStorage, Agent } from './agent-auth-api';
import { authStorage } from './storage';

export type UserRole = 'agent' | 'seller' | 'external-agent' | 'admin' | 'marketing' | 'deal-coordinator' | 'public';

export interface User {
  id: string;
  username: string;
  password: string;
  role: UserRole;
  name: string;
  email: string;
}

// Authentication service
class AuthService {
  private currentUser: User | null = null;
  private listeners: Array<(user: User | null) => void> = [];

  constructor() {
    // Try to restore session from authStorage
    if (typeof window !== 'undefined') {
      const savedUser = authStorage.get<User>('current_user');
      if (savedUser) {
        // Detect truncated Cognito IDs from before F-051 fix — clear and force re-login
        if (savedUser.id === 'cognito-' || savedUser.id === 'cognito-undefined') {
          authStorage.remove('current_user');
        } else {
          this.currentUser = savedUser;
        }
      }
    }
  }

  /**
   * @deprecated Use cognitoLogin() from cognito-auth.ts instead.
   * This method is kept only as a no-op fallback — all real logins go through Cognito.
   */
  login(_username: string, _password: string): User | null {
    console.warn('[AuthService.login] Local login is deprecated. Use Cognito login.');
    return null;
  }

  logout() {
    this.currentUser = null;
    // Also clear agent session storage for backward compat
    agentSessionStorage.clearSession();
    if (typeof window !== 'undefined') {
      authStorage.remove('current_user');
    }
    this.notifyListeners();
  }

  // Set current user directly (used by Cognito auth bridge and seller auth flow)
  setCurrentUser(user: User) {
    this.currentUser = user;
    if (typeof window !== 'undefined') {
      authStorage.set('current_user', user);
    }
    this.notifyListeners();
  }

  getCurrentUser(): User | null {
    return this.currentUser;
  }

  /**
   * Get agent info from session storage (backward compat).
   * After Cognito migration, agent data is populated in agentSessionStorage
   * by bridgeCognitoToAgentSession() after login.
   */
  getCurrentAgent(): Agent | null {
    return agentSessionStorage.getAgentInfo();
  }

  isAuthenticated(): boolean {
    return this.currentUser !== null;
  }

  /**
   * Check if the current user is authenticated as an agent.
   * After Cognito migration, this checks both the main user session
   * and the compatibility agentSessionStorage.
   */
  isAgentAuthenticated(): boolean {
    return (this.currentUser?.role === 'agent') || agentSessionStorage.hasSession();
  }

  hasRole(role: UserRole): boolean {
    return this.currentUser?.role === role;
  }

  subscribe(listener: (user: User | null) => void) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  private notifyListeners() {
    this.listeners.forEach(listener => listener(this.currentUser));
  }
}

export const authService = new AuthService();